import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, shareReplay, startWith, tap } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { Buyer, BuyerAddress } from '../models/buyer.model';

@Injectable({
  providedIn: 'root'
})
export class BuyersApiService {

  buyers: BehaviorSubject<Buyer[]> = new BehaviorSubject([]);
  buyers$ = this.buyers.pipe(shareReplay(1));
  buyersByAddress$ = this.buyers.pipe(
    map(buyers =>
      // generate duplicate objects, one per address
      buyers.flatMap(buyer =>
        buyer.addresses.map(address => Object.assign({}, buyer, { address }) as BuyerAddress)
      )
    )
  );
  loading = new ReplaySubject<boolean>(1);
  loading$ = this.loading.pipe(startWith(false), distinctUntilChanged(), shareReplay(1));
  lastUpdate = 0;

  $selectedId = new ReplaySubject<number>(1);
  selectedId$ = this.$selectedId.pipe(distinctUntilChanged(), shareReplay(1));
  selected$ = combineLatest([
    this.buyers$,
    this.selectedId$
  ])
    .pipe(
      map(([buyers, id]) => id && buyers.find(b => b.id === id)),
      filter(x => !!x)
    );
  currencyCode$ = this.selected$.pipe(pluck('currencyCode'));

  constructor(private http: HttpClient) {
    this.update();
  }

  select(id: number) {
    if (id) {
      this.$selectedId.next(id);
    }
  }

  update(): BehaviorSubject<Buyer[]> {
    const now = Date.now();
    if (now - this.lastUpdate > 60 * 1000) {
      this.lastUpdate = now;
      this.fetch();
    }
    return this.buyers;
  }

  fetch() {
    this.loading.next(true);
    this.http.get<Buyer[]>(`${env.apiPath}/buyers`)
      .toPromise()
      .then(data => {
        this.buyers.next(data);
        this.loading.next(false);
      })
      .catch(error => {
        this.loading.next(false);
      });
  }

}
